import React from 'react';

import {
  H1,
  H2,
  Preamble,
  Table,
  TableHead,
  TableHeadCell,
  TableBody,
  TableCell,
  TableRow,
  Subtitle2,
  SubText,
  Link,
  Code,
  Paragraph,
} from '@aurora/widgets-react';

import { OpenOutline } from '@aurora/icons-react';
//import { Code } from '@aurora/widgets-react/dist/widgets-react.production';

const Products = () => (
  <React.Fragment>
    <H1>Products</H1>

    <Preamble>
      A product can be seen as a bundle of parameters. This page describes which
      parameters a product consists of. It also describes available typecases
      for each product. See{' '}
      <Link to="https://www.smhi.se/professionella-tjanster/professionella-tjanster/fastighet/normalarskorrigering-varme">
        Energy Index information <OpenOutline />
      </Link>{' '}
      and{' '}
      <Link to="https://www.smhi.se/professionella-tjanster/professionella-tjanster/fastighet/smhi-kyl-index">
        Cooling Index information <OpenOutline />
      </Link>{' '}
      for details about these typecases.
    </Preamble>

    <Paragraph>
      All products applies to the <Code>history</Code> service except the{' '}
      <b>Forecast Products</b>. Also note that all parameters are not typecase
      dependent.
    </Paragraph>

    <H2>Hourly Products</H2>

    <Table>
      <TableHead>
        <TableHeadCell>
          <Subtitle2>Product</Subtitle2>
        </TableHeadCell>
        <TableHeadCell>
          <Subtitle2>parameternrs</Subtitle2>
        </TableHeadCell>
        <TableHeadCell>
          <Subtitle2>typecases</Subtitle2>
        </TableHeadCell>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell>Temperature</TableCell>
          <TableCell>5</TableCell>
          <TableCell></TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Equivalent Temperature (ET)</TableCell>
          <TableCell>5, 322</TableCell>
          <TableCell>105</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Cooling Index (Kyl-Index)</TableCell>
          <TableCell>5, 316</TableCell>
          <TableCell>1016, 1017, 1018, 1019, 1020</TableCell>
        </TableRow>
      </TableBody>
    </Table>

    <H2>Daily Products</H2>

    <Table>
      <TableHead>
        <TableHeadCell>
          <Subtitle2>Product</Subtitle2>
        </TableHeadCell>
        <TableHeadCell>
          <Subtitle2>parameternrs</Subtitle2>
        </TableHeadCell>
        <TableHeadCell>
          <Subtitle2>typecases</Subtitle2>
        </TableHeadCell>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell>Energy Index (Energi-Index)</TableCell>
          <TableCell>4331, 5331, 25331, 1005, 21320</TableCell>
          <TableCell>1020, 1021, 1022, 1023, 1024</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Heating Degree Days (Graddagar)</TableCell>
          <TableCell>4330, 5330, 25330, 1005, 21320</TableCell>
          <TableCell>1107, 1109, 1111, 1113, 1115, 1117*</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Cooling Index (Kyl-Index)</TableCell>
          <TableCell>4316, 5316, 25316, 1005, 21320</TableCell>
          <TableCell>1016, 1017, 1018, 1019, 1020</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Average Temperature</TableCell>
          <TableCell>1005</TableCell>
          <TableCell></TableCell>
        </TableRow>
      </TableBody>
    </Table>

    <Paragraph />

    <SubText>
      * These typecases are only defined by their balance temperatures and are
      defined by the last 2 numbers. Typecase 1117 has 17 &deg;C balance
      temperature and 1115 has 15 &deg;C, and so on... These typecases are only
      used to calculate Heating Degree Days.
    </SubText>

    <H2>Monthly Products</H2>

    <Table>
      <TableHead>
        <TableHeadCell>
          <Subtitle2>Product</Subtitle2>
        </TableHeadCell>
        <TableHeadCell>
          <Subtitle2>parameternrs</Subtitle2>
        </TableHeadCell>
        <TableHeadCell>
          <Subtitle2>typecases Bas</Subtitle2>
        </TableHeadCell>
        <TableHeadCell>
          <Subtitle2>typecases Plus</Subtitle2>
        </TableHeadCell>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell>Energy Index (Energi-Index)</TableCell>
          <TableCell>18331, 13331, 16331, 28331, 3005, 23320</TableCell>
          <TableCell>1024</TableCell>
          <TableCell>1020, 1021, 1022, 1023, 1024</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Heating Degree Days (Graddagar)</TableCell>
          <TableCell>18330, 13330, 16330, 28330, 3005, 23320</TableCell>
          <TableCell>1117*</TableCell>
          <TableCell>1107, 1109, 1111, 1113, 1115, 1117*</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Cooling Index (Kyl-Index)</TableCell>
          <TableCell>18316, 13316, 16316, 28316, 3005, 23320</TableCell>
          <TableCell>1016, 1017, 1018, 1019, 1020</TableCell>
          <TableCell>-</TableCell>
        </TableRow>
      </TableBody>
    </Table>

    <Paragraph />

    <SubText>
      * These typecases are only defined by their balance temperatures and are
      defined by the last 2 numbers. Typecase 1117 has 17 &deg;C balance
      temperature and 1115 has 15 &deg;C, and so on... These typecases are only
      used to calculate Heating Degree Days.
    </SubText>

    <H2>Forecast Products</H2>

    <Paragraph>
      Forecast products applies to the <Code>forecast</Code> service. Note that
      all parameters are not typecase dependent.
    </Paragraph>

    <Table>
      <TableHead>
        <TableHeadCell>
          <Subtitle2>Product</Subtitle2>
        </TableHeadCell>
        <TableHeadCell>
          <Subtitle2>parameternrs</Subtitle2>
        </TableHeadCell>
        <TableHeadCell>
          <Subtitle2>typecases</Subtitle2>
        </TableHeadCell>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell>Equivalent Temperature (ET)</TableCell>
          <TableCell>5, 322</TableCell>
          <TableCell>105</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Forecast Control</TableCell>
          <TableCell>5, 325</TableCell>
          <TableCell>
            27 different typecases. A separate list will be provided.
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </React.Fragment>
);

export default Products;
